import { useState, useMemo, useEffect, Fragment, useRef } from 'react'
import { formatDistanceToNow } from 'date-fns'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid'
import clsx from 'clsx'
import type { ConfigurationDrift, DriftSeverity, DriftStatus } from '@/types'
import apiClient from '@/lib/api'
import toast from 'react-hot-toast'
import RejectionNotificationModal from '@/components/RejectionNotificationModal'
import BulkActionCommentModal from '@/components/Drift/BulkActionCommentModal'
import DriftRefinePopover, { type DriftFilterValues } from '@/components/Drift/DriftFilters'

/* ────────────────────────────────────────────────────────────────────
   Types & local maps
   ──────────────────────────────────────────────────────────────────── */

interface ResourceGroup {
  groupKey: string
  component: string
  componentName: string
  drifts: ConfigurationDrift[]
  highestSeverity: DriftSeverity
  statusSummary: Record<string, number>
  latestDetection: string
}

const severityOrder: Record<DriftSeverity, number> = {
  Critical: 4,
  High: 3,
  Medium: 2,
  Low: 1,
}

// sev-pill / status-pill / group-rail color tokens — values mirror the
// `.sev-pill` / `.status-pill` rules in 01b-section3-restyle.html so we
// keep visual parity with the approved mockup without inventing tokens.
type SevKey = 'crit' | 'high' | 'med' | 'low'
const SEV_KEY: Record<DriftSeverity, SevKey> = {
  Critical: 'crit',
  High: 'high',
  Medium: 'med',
  Low: 'low',
}

const SEV_PILL: Record<SevKey, { bg: string; border: string; text: string }> = {
  crit: {
    bg: 'rgba(229,115,115,0.08)',
    border: 'rgba(229,115,115,0.4)',
    text: 'var(--color-red-deep, #C84B4B)',
  },
  high: {
    bg: 'rgba(235,212,180,0.18)',
    border: 'rgba(201,173,128,0.5)',
    text: 'var(--color-sand-deep, #C9AD80)',
  },
  med: {
    bg: 'rgba(167,211,240,0.18)',
    border: 'rgba(123,168,196,0.4)',
    text: 'var(--color-blue-deep, #7BA8C4)',
  },
  low: {
    bg: 'rgba(118,214,157,0.12)',
    border: 'rgba(74,155,110,0.4)',
    text: 'var(--color-green-deep, #4A9B6E)',
  },
}

const SEV_RAIL_COLOR: Record<SevKey, string> = {
  crit: 'var(--color-red-deep, #C84B4B)',
  high: 'var(--color-sand-deep, #C9AD80)',
  med: 'var(--color-blue-deep, #7BA8C4)',
  low: 'var(--color-green-deep, #4A9B6E)',
}

type StatusTone = 'new' | 'pending' | 'approved' | 'awaiting' | 'neutral'
const STATUS_TONE: Record<string, StatusTone> = {
  New: 'new',
  Pending: 'pending',
  PendingApproval: 'pending',
  Approved: 'approved',
  AwaitingRemediation: 'awaiting',
  Rejected: 'neutral',
  Remediated: 'neutral',
  Ignored: 'neutral',
}

const STATUS_PILL: Record<StatusTone, { bg: string; border: string; text: string }> = {
  new: {
    bg: 'rgba(118,214,157,0.12)',
    border: 'rgba(74,155,110,0.4)',
    text: 'var(--color-green-deep, #4A9B6E)',
  },
  pending: {
    bg: 'rgba(167,211,240,0.18)',
    border: 'rgba(123,168,196,0.4)',
    text: 'var(--color-blue-deep, #7BA8C4)',
  },
  awaiting: {
    bg: 'rgba(235,212,180,0.18)',
    border: 'rgba(201,173,128,0.5)',
    text: 'var(--color-sand-deep, #C9AD80)',
  },
  approved: {
    bg: 'var(--color-paper-warm, #FBF8F2)',
    border: 'var(--color-rule, #E6E1D5)',
    text: 'var(--color-navy, #212938)',
  },
  neutral: {
    bg: 'var(--color-paper-warm, #FBF8F2)',
    border: 'var(--color-rule, #E6E1D5)',
    text: 'var(--color-mute, #6B7280)',
  },
}

const SEVERITY_CHIPS: ReadonlyArray<{
  value: string
  label: string
  sev: SevKey | null
  countKey: 'critical' | 'high' | 'medium' | 'low' | null
}> = [
  { value: '', label: 'All', sev: null, countKey: null },
  { value: 'Critical', label: 'Critical', sev: 'crit', countKey: 'critical' },
  { value: 'High', label: 'High', sev: 'high', countKey: 'high' },
  { value: 'Medium', label: 'Medium', sev: 'med', countKey: 'medium' },
  { value: 'Low', label: 'Low', sev: 'low', countKey: 'low' },
]

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function formatStatusLabel(status: string): string {
  switch (status) {
    case 'PendingApproval':
      return 'Pending'
    case 'AwaitingRemediation':
      return 'Awaiting'
    default:
      return status.replace(/([A-Z])/g, ' $1').trim()
  }
}

/* ────────────────────────────────────────────────────────────────────
   Small presentational primitives
   ──────────────────────────────────────────────────────────────────── */

export function SevPill({ severity }: { severity: DriftSeverity }) {
  const key = SEV_KEY[severity]
  const tone = SEV_PILL[key]
  return (
    <span
      className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
      style={{
        background: tone.bg,
        borderColor: tone.border,
        color: tone.text,
      }}
    >
      {severity}
    </span>
  )
}

export function StatusPill({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? 'neutral'
  const palette = STATUS_PILL[tone]
  return (
    <span
      className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
      style={{
        background: palette.bg,
        borderColor: palette.border,
        color: palette.text,
      }}
    >
      {formatStatusLabel(status)}
    </span>
  )
}

function GsPill({ label, count }: { label: string; count: number }) {
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] font-bold tabular-nums"
      style={{
        background: 'var(--color-paper, #fff)',
        borderColor: 'var(--color-rule, #E6E1D5)',
        color: 'var(--color-mute, #6B7280)',
      }}
    >
      <span
        className="mr-1 font-sans text-[9px] font-bold uppercase tracking-[0.12em]"
        style={{ color: 'var(--color-faint, #9CA3AF)' }}
      >
        {label}
      </span>
      {count}
    </span>
  )
}

function DriftCheckbox({
  checked,
  indeterminate,
  onChange,
  className,
  ariaLabel,
}: {
  checked: boolean
  indeterminate?: boolean
  onChange: (checked: boolean) => void
  className?: string
  ariaLabel?: string
}) {
  const ref = useRef<HTMLButtonElement>(null)
  const filled = checked || indeterminate

  return (
    <button
      ref={ref}
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={ariaLabel}
      onClick={(e) => {
        e.stopPropagation()
        onChange(!checked)
      }}
      className={clsx(
        'relative inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[4px] border-[1.5px]',
        'transition-colors duration-150 focus:outline-none',
        'focus-visible:ring-2 focus-visible:ring-offset-1',
        filled
          ? 'bg-[color:var(--color-navy,#212938)] border-[color:var(--color-navy,#212938)]'
          : 'bg-[color:var(--color-paper,#fff)] border-[color:var(--color-rule,#E6E1D5)] hover:border-[color:var(--color-navy,#212938)]',
        className,
      )}
      style={{
        // Tailwind doesn't carry CSS-var into ring tokens, so set focus ring color inline
        ['--tw-ring-color' as string]: 'rgba(33,41,56,0.4)',
      }}
    >
      {checked && !indeterminate && (
        <svg className="h-3 w-3 text-[color:var(--color-paper,#fff)]" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="M2.5 6L5 8.5L9.5 3.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {indeterminate && <span className="block h-[2px] w-2 rounded-full bg-[color:var(--color-paper,#fff)]" />}
    </button>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Header bands — resting (chip rail + Refine) and selected (bulk bar)
   ──────────────────────────────────────────────────────────────────── */

function QtHead({
  severity,
  onSeverityChange,
  severityCounts,
  totalActive,
  refineFilters,
  onRefineChange,
}: {
  severity: string
  onSeverityChange: (value: string) => void
  severityCounts?: { critical: number; high: number; medium: number; low: number }
  totalActive: number
  refineFilters: DriftFilterValues
  onRefineChange: (filters: DriftFilterValues) => void
}) {
  return (
    <div
      className="flex flex-wrap items-end justify-between gap-3 border-b px-7 pb-4 pt-5"
      style={{ borderColor: 'var(--color-rule, #E6E1D5)' }}
    >
      <div className="flex flex-wrap gap-2">
        {SEVERITY_CHIPS.map((chip) => {
          const isActive = chip.value === severity
          const count =
            chip.value === ''
              ? totalActive
              : chip.countKey && severityCounts
                ? severityCounts[chip.countKey]
                : null
          return (
            <button
              key={chip.value || 'all'}
              type="button"
              onClick={() => onSeverityChange(chip.value)}
              className={clsx(
                'inline-flex items-center gap-2 rounded-full border px-3 py-1.5',
                'text-[10px] font-bold uppercase tracking-[0.16em]',
                'transition-colors duration-150',
                isActive
                  ? 'border-[color:var(--color-navy,#212938)] bg-[color:var(--color-navy,#212938)] text-[color:var(--color-paper,#fff)]'
                  : 'border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper-warm,#FBF8F2)] text-[color:var(--color-navy,#212938)] hover:border-[color:var(--color-navy,#212938)]',
              )}
            >
              {chip.sev && (
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 rounded-full"
                  style={{
                    background: isActive
                      ? 'color-mix(in srgb, var(--color-paper, #fff) 85%, transparent)'
                      : SEV_RAIL_COLOR[chip.sev],
                  }}
                />
              )}
              <span>{chip.label}</span>
              {count != null && (
                <span
                  className="font-mono text-[10px] font-bold tabular-nums"
                  style={{
                    color: isActive
                      ? 'color-mix(in srgb, var(--color-paper, #fff) 70%, transparent)'
                      : 'var(--color-mute, #6B7280)',
                  }}
                >
                  {pad2(count)}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div className="flex items-center gap-2">
        <DriftRefinePopover filters={refineFilters} onFilterChange={onRefineChange} />
      </div>
    </div>
  )
}

interface VerbButton {
  key: string
  label: string
  status: DriftStatus
  tone?: 'approve' | 'reject' | 'neutral'
}

const VERB_BUTTONS: ReadonlyArray<VerbButton> = [
  { key: 'approve', label: 'Approve', status: 'Approved' as DriftStatus, tone: 'approve' },
  { key: 'reject', label: 'Reject', status: 'Rejected' as DriftStatus, tone: 'reject' },
  {
    key: 'awaiting',
    label: 'Awaiting remediation',
    status: 'AwaitingRemediation' as DriftStatus,
    tone: 'neutral',
  },
  {
    key: 'remediated',
    label: 'Mark remediated',
    status: 'Remediated' as DriftStatus,
    tone: 'neutral',
  },
  { key: 'new', label: 'Mark new', status: 'New' as DriftStatus, tone: 'neutral' },
  { key: 'ignore', label: 'Ignore', status: 'Ignored' as DriftStatus, tone: 'neutral' },
]

function QtBulkBar({
  selectedCount,
  onAction,
  onClear,
  inProgress,
  archiveMode,
}: {
  selectedCount: number
  onAction: (status: DriftStatus) => void
  onClear: () => void
  inProgress: boolean
  archiveMode: boolean
}) {
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-4 px-7 py-3.5"
      style={{ background: '#212938', color: '#fff' }}
    >
      <div className="inline-flex items-center gap-3">
        {archiveMode ? (
          <span
            className="font-serif text-[12px] italic"
            style={{ color: 'rgba(255,255,255,0.65)' }}
          >
            Read-only · archive view
          </span>
        ) : (
          <>
            <span
              className="font-mono text-[22px] font-extrabold tabular-nums"
              style={{ color: 'var(--color-primary-500, #76D69D)' }}
            >
              {pad2(selectedCount)}
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.16em]">
              {selectedCount === 1 ? 'drift selected' : 'drifts selected'}
            </span>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {VERB_BUTTONS.map((verb) => {
          const disabled = inProgress || archiveMode
          return (
            <button
              key={verb.key}
              type="button"
              onClick={() => onAction(verb.status)}
              disabled={disabled}
              className={clsx(
                'inline-flex items-center rounded-full border px-3 py-1.5',
                'text-[10px] font-bold uppercase tracking-[0.14em]',
                'transition-colors duration-150',
                'disabled:cursor-not-allowed disabled:opacity-40',
              )}
              style={
                verb.tone === 'approve'
                  ? {
                      background: 'rgba(118,214,157,0.06)',
                      borderColor: 'rgba(118,214,157,0.4)',
                      color: 'var(--color-primary-500, #76D69D)',
                    }
                  : verb.tone === 'reject'
                    ? {
                        background: 'rgba(229,115,115,0.06)',
                        borderColor: 'rgba(229,115,115,0.4)',
                        color: '#FF9E9E',
                      }
                    : {
                        background: 'rgba(255,255,255,0.06)',
                        borderColor: 'rgba(255,255,255,0.16)',
                        color: '#fff',
                      }
              }
            >
              {verb.label}
            </button>
          )
        })}
        <button
          type="button"
          onClick={onClear}
          disabled={inProgress}
          className="inline-flex items-center rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            background: 'transparent',
            borderColor: 'rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.6)',
          }}
        >
          Clear
        </button>
      </div>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Main component
   ──────────────────────────────────────────────────────────────────── */

interface Props {
  drifts: ConfigurationDrift[]
  isLoading: boolean
  onRowClick: (id: string) => void
  selectedId: string | null
  pagination: {
    current: number
    total: number
    pageSize: number
    onPageChange: (page: number) => void
  }
  onRefresh: () => void
  filters: DriftFilterValues
  onFilterChange: (filters: DriftFilterValues) => void
  severityCounts?: { critical: number; high: number; medium: number; low: number }
  archiveMode?: boolean
}

export default function DriftTableWithBulkActions({
  drifts,
  isLoading,
  onRowClick,
  selectedId,
  pagination,
  onRefresh,
  filters,
  onFilterChange,
  severityCounts,
  archiveMode = false,
}: Props) {
  const [selectedDrifts, setSelectedDrifts] = useState<Set<string>>(new Set())
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false)
  const [showImpactWarning, setShowImpactWarning] = useState(false)
  const [bulkApprovalImpact, setBulkApprovalImpact] = useState<any>(null)
  const [showRejectionNotification, setShowRejectionNotification] = useState(false)
  const [rejectedDriftCount, setRejectedDriftCount] = useState(0)
  const [showCommentModal, setShowCommentModal] = useState(false)
  const [pendingBulkAction, setPendingBulkAction] = useState<DriftStatus | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [hasInitializedExpansion, setHasInitializedExpansion] = useState(false)

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.pageSize))

  // Group drifts by resource (component:componentName)
  const groupedDrifts = useMemo(() => {
    const groups = new Map<string, ResourceGroup>()

    drifts.forEach((drift) => {
      const groupKey = `${drift.component}:${drift.componentName || drift.component}`

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          groupKey,
          component: drift.component,
          componentName: drift.componentName || drift.component,
          drifts: [],
          highestSeverity: 'Low' as DriftSeverity,
          statusSummary: {},
          latestDetection: drift.detectedAt,
        })
      }

      const group = groups.get(groupKey)!
      group.drifts.push(drift)

      if (severityOrder[drift.severity] > severityOrder[group.highestSeverity]) {
        group.highestSeverity = drift.severity
      }

      const status = drift.status
      group.statusSummary[status] = (group.statusSummary[status] || 0) + 1

      if (new Date(drift.detectedAt) > new Date(group.latestDetection)) {
        group.latestDetection = drift.detectedAt
      }
    })

    return Array.from(groups.values())
  }, [drifts])

  const multiPropertyGroups = useMemo(
    () => groupedDrifts.filter((g) => g.drifts.length > 1),
    [groupedDrifts],
  )
  const singlePropertyDrifts = useMemo(
    () => groupedDrifts.filter((g) => g.drifts.length === 1).map((g) => g.drifts[0]),
    [groupedDrifts],
  )

  // Smart default expansion: expand if ≤3 multi-property groups
  useEffect(() => {
    if (!hasInitializedExpansion && multiPropertyGroups.length > 0) {
      if (multiPropertyGroups.length <= 3) {
        setExpandedGroups(new Set(multiPropertyGroups.map((g) => g.groupKey)))
      }
      setHasInitializedExpansion(true)
    }
  }, [multiPropertyGroups, hasInitializedExpansion])

  const isGroupFullySelected = (group: ResourceGroup) =>
    group.drifts.length > 0 && group.drifts.every((d) => selectedDrifts.has(d.id))

  const isGroupPartiallySelected = (group: ResourceGroup) =>
    group.drifts.some((d) => selectedDrifts.has(d.id)) &&
    !group.drifts.every((d) => selectedDrifts.has(d.id))

  const handleSelectGroup = (group: ResourceGroup, checked: boolean) => {
    const newSelection = new Set(selectedDrifts)
    group.drifts.forEach((drift) => {
      if (checked) newSelection.add(drift.id)
      else newSelection.delete(drift.id)
    })
    setSelectedDrifts(newSelection)
  }

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      return next
    })
  }

  const pendingDrifts = drifts.filter(
    (drift) => drift.status === 'Pending' || drift.status === 'PendingApproval',
  )
  const hasPendingDrifts = pendingDrifts.length > 0

  const handleSelectAll = (checked: boolean) => {
    if (checked) setSelectedDrifts(new Set(drifts.map((d) => d.id)))
    else setSelectedDrifts(new Set())
  }

  const handleSelectDrift = (driftId: string, checked: boolean) => {
    const newSelection = new Set(selectedDrifts)
    if (checked) newSelection.add(driftId)
    else newSelection.delete(driftId)
    setSelectedDrifts(newSelection)
  }

  const handleCancelPendingApprovals = async () => {
    const confirmed = window.confirm(
      `Cancel ${pendingDrifts.length} pending approval(s) and reset them to New status?`,
    )
    if (!confirmed) return

    setBulkActionInProgress(true)
    try {
      const pendingDriftIds = pendingDrifts.map((d) => d.id)

      await apiClient.post('/configurationdrift/bulk-status-change', {
        driftIds: pendingDriftIds,
        newStatus: 'New',
        approvedBy: 'Current User',
        comments: 'Cancelled pending approval - reset to New',
      })

      toast.success(`Successfully cancelled ${pendingDriftIds.length} pending approvals`)
      onRefresh()
    } catch (error: any) {
      console.error('Cancel pending approvals error:', error)
      toast.error(error.response?.data?.error || 'Failed to cancel pending approvals')
    } finally {
      setBulkActionInProgress(false)
    }
  }

  const handleBulkStatusChange = async (newStatus: DriftStatus) => {
    if (selectedDrifts.size === 0) {
      toast.error('No drifts selected')
      return
    }

    if (newStatus === 'Approved' && !showImpactWarning) {
      setBulkActionInProgress(true)
      try {
        const impactResponse = await apiClient.post('/configurationdrift/bulk-approval-impact', {
          driftIds: Array.from(selectedDrifts),
        })

        if (impactResponse.data.hasResourceLevelUpdates) {
          setBulkApprovalImpact(impactResponse.data)
          setShowImpactWarning(true)
          setBulkActionInProgress(false)
          return
        }
      } catch (error) {
        console.error('Failed to check bulk approval impact:', error)
      }
      setBulkActionInProgress(false)
    }

    if (!showImpactWarning) {
      setPendingBulkAction(newStatus)
      setShowCommentModal(true)
      return
    }

    setPendingBulkAction(newStatus)
    setShowCommentModal(true)
  }

  const executeBulkStatusChange = async (comment: string) => {
    if (!pendingBulkAction) return

    const newStatus = pendingBulkAction
    setShowCommentModal(false)
    setBulkActionInProgress(true)

    try {
      const response = await apiClient.post('/configurationdrift/bulk-status-change', {
        driftIds: Array.from(selectedDrifts),
        newStatus,
        approvedBy: 'Current User',
        comments: comment || `Bulk ${newStatus.toLowerCase()} action`,
      })

      toast.success(response.data.message || `Successfully updated ${selectedDrifts.size} drifts`)

      if (newStatus === 'Rejected') {
        setRejectedDriftCount(selectedDrifts.size)
        setShowRejectionNotification(true)
      }

      setSelectedDrifts(new Set())
      setShowImpactWarning(false)
      setBulkApprovalImpact(null)
      setPendingBulkAction(null)

      setTimeout(() => {
        onRefresh()
      }, 500)
    } catch (error: any) {
      console.error('Bulk status change error:', error)
      toast.error(error.response?.data?.error || 'Failed to update drift statuses')
    } finally {
      setBulkActionInProgress(false)
      setShowImpactWarning(false)
    }
  }

  /* ────────────────────────────
     Loading state
     ──────────────────────────── */
  if (isLoading) {
    return (
      <div
        className="overflow-hidden rounded-3xl border bg-[color:var(--color-paper,#fff)] shadow-[0_1px_2px_rgba(33,41,56,0.06),0_24px_60px_-10px_rgba(33,41,56,0.12)]"
        style={{ borderColor: 'var(--color-rule, #E6E1D5)' }}
      >
        <div className="animate-pulse p-7">
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <div
                  className="h-4 w-16 rounded"
                  style={{ background: 'var(--color-rule-soft, #F0EBDD)' }}
                />
                <div
                  className="h-4 flex-1 rounded"
                  style={{ background: 'var(--color-rule-soft, #F0EBDD)' }}
                />
                <div
                  className="h-4 w-20 rounded"
                  style={{ background: 'var(--color-rule-soft, #F0EBDD)' }}
                />
                <div
                  className="h-4 w-24 rounded"
                  style={{ background: 'var(--color-rule-soft, #F0EBDD)' }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const showFromIndex = (pagination.current - 1) * pagination.pageSize + 1
  const showToIndex = Math.min(pagination.current * pagination.pageSize, pagination.total)
  const driftLabel = archiveMode ? 'remediated drifts' : 'active drifts'

  /* ────────────────────────────
     Render
     ──────────────────────────── */
  return (
    <div
      className="overflow-hidden rounded-3xl border bg-[color:var(--color-paper,#fff)] shadow-[0_1px_2px_rgba(33,41,56,0.06),0_24px_60px_-10px_rgba(33,41,56,0.12)]"
      style={{ borderColor: 'var(--color-rule, #E6E1D5)' }}
    >
      {/* Header band — chip rail OR bulk bar */}
      {selectedDrifts.size === 0 ? (
        <QtHead
          severity={filters.severity}
          onSeverityChange={(value) => onFilterChange({ ...filters, severity: value })}
          severityCounts={severityCounts}
          totalActive={pagination.total}
          refineFilters={filters}
          onRefineChange={onFilterChange}
        />
      ) : (
        <QtBulkBar
          selectedCount={selectedDrifts.size}
          onAction={handleBulkStatusChange}
          onClear={() => setSelectedDrifts(new Set())}
          inProgress={bulkActionInProgress}
          archiveMode={archiveMode}
        />
      )}

      {/* Pending Approvals notice (sand-deep tone) */}
      {hasPendingDrifts && (
        <div
          className="mx-7 my-5 grid items-center gap-7 rounded-[4px] border px-7 py-5"
          style={{
            background: 'var(--color-paper-warm, #FBF8F2)',
            borderColor: 'var(--color-rule, #E6E1D5)',
            borderLeft: '4px solid var(--color-sand-deep, #C9AD80)',
            gridTemplateColumns: '1fr auto',
          }}
        >
          <div>
            <div
              className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.22em]"
              style={{ color: 'var(--color-mute, #6B7280)' }}
            >
              Heads up
            </div>
            <h4
              className="text-lg font-extrabold leading-tight"
              style={{ color: 'var(--color-navy, #212938)' }}
            >
              {pendingDrifts.length === 1 ? 'One change is ' : `${pendingDrifts.length} changes are `}
              <em
                className="font-serif font-semibold not-italic"
                style={{
                  fontStyle: 'italic',
                  color: 'var(--color-sand-deep, #C9AD80)',
                  fontFamily: 'var(--font-serif, "Fraunces", "Inter", serif)',
                }}
              >
                queuing up.
              </em>
            </h4>
            <p
              className="mt-1.5 max-w-[560px] text-xs"
              style={{ color: 'var(--color-mute, #6B7280)' }}
            >
              Pending approvals wait for the next batch run. You can cancel and reset them to{' '}
              <strong style={{ color: 'var(--color-navy, #212938)', fontWeight: 600 }}>New</strong>{' '}
              if they shouldn't go through.
            </p>
          </div>
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={onRefresh}
              disabled={bulkActionInProgress}
              className="inline-flex items-center rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors duration-150 disabled:opacity-50"
              style={{
                background: 'var(--color-paper, #fff)',
                borderColor: 'var(--color-rule, #E6E1D5)',
                color: 'var(--color-navy, #212938)',
              }}
            >
              Refresh status
            </button>
            <button
              type="button"
              onClick={handleCancelPendingApprovals}
              disabled={bulkActionInProgress}
              className="inline-flex items-center rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white transition-colors duration-150 disabled:opacity-50"
              style={{
                background: 'var(--color-sand-deep, #C9AD80)',
                borderColor: 'var(--color-sand-deep, #C9AD80)',
              }}
            >
              Cancel pending
            </button>
          </div>
        </div>
      )}

      {/* Bulk Approval Impact Warning notice (blue-deep tone) */}
      {showImpactWarning && bulkApprovalImpact && (
        <div
          className="mx-7 my-5 grid gap-5 rounded-[4px] border px-7 py-5"
          style={{
            background: 'var(--color-paper-warm, #FBF8F2)',
            borderColor: 'var(--color-rule, #E6E1D5)',
            borderLeft: '4px solid var(--color-blue-deep, #7BA8C4)',
            gridTemplateColumns: 'auto 1fr',
          }}
        >
          <div className="pt-1">
            <ExclamationTriangleIcon
              className="h-5 w-5"
              style={{ color: 'var(--color-blue-deep, #7BA8C4)' }}
            />
          </div>
          <div>
            <div
              className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.22em]"
              style={{ color: 'var(--color-mute, #6B7280)' }}
            >
              Approval impact
            </div>
            <h4
              className="text-lg font-extrabold leading-tight"
              style={{ color: 'var(--color-navy, #212938)' }}
            >
              Resource-level{' '}
              <em
                className="font-serif font-semibold"
                style={{
                  fontStyle: 'italic',
                  color: 'var(--color-blue-deep, #7BA8C4)',
                  fontFamily: 'var(--font-serif, "Fraunces", "Inter", serif)',
                }}
              >
                updates will trigger.
              </em>
            </h4>
            <div
              className="mt-3 space-y-3 text-xs"
              style={{ color: 'var(--color-mute, #6B7280)' }}
            >
              <p
                className="font-medium"
                style={{ color: 'var(--color-navy, #212938)' }}
              >
                {bulkApprovalImpact.warning}
              </p>

              {bulkApprovalImpact.impactedResources?.length > 0 && (
                <div>
                  <p
                    className="mb-2 font-medium"
                    style={{ color: 'var(--color-navy, #212938)' }}
                  >
                    Resources with complex property additions:
                  </p>
                  {(() => {
                    const groupedResources = new Map<string, any>()

                    bulkApprovalImpact.impactedResources.forEach((resource: any) => {
                      const key = `${resource.component}:${resource.componentName}`

                      if (!groupedResources.has(key)) {
                        groupedResources.set(key, {
                          component: resource.component,
                          componentName: resource.componentName,
                          settings: new Set(),
                          allAdditionalDrifts: new Map(),
                        })
                      }

                      const group = groupedResources.get(key)
                      group.settings.add(resource.setting)

                      resource.additionalDriftsAffected?.forEach((drift: any) => {
                        group.allAdditionalDrifts.set(drift.id, drift)
                      })
                    })

                    return Array.from(groupedResources.entries()).map(([key, group]) => (
                      <div
                        key={key}
                        className="mb-3 border-l-2 pl-4"
                        style={{ borderColor: 'rgba(123,168,196,0.5)' }}
                      >
                        <p
                          className="font-medium"
                          style={{ color: 'var(--color-navy, #212938)' }}
                        >
                          {group.component}: {group.componentName}
                        </p>
                        <p>Settings: {Array.from(group.settings).join(', ')}</p>
                        {group.allAdditionalDrifts.size > 0 && (
                          <div className="mt-1">
                            <p>Additional drifts that will be included:</p>
                            <ul className="mt-1 list-inside list-disc">
                              {Array.from(group.allAdditionalDrifts.values()).map((drift: any) => (
                                <li key={drift.id}>
                                  {drift.setting}
                                  <span
                                    className="ml-1 rounded px-1 py-0.5 text-[10px]"
                                    style={{
                                      background: 'rgba(123,168,196,0.18)',
                                      color: 'var(--color-blue-deep, #7BA8C4)',
                                    }}
                                  >
                                    {drift.severity}
                                  </span>
                                  <span
                                    className="ml-1"
                                    style={{ color: 'var(--color-faint, #9CA3AF)' }}
                                  >
                                    ({drift.status})
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))
                  })()}
                </div>
              )}

              {bulkApprovalImpact.recommendation && (
                <p className="italic">{bulkApprovalImpact.recommendation}</p>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowImpactWarning(false)
                  handleBulkStatusChange('Approved' as DriftStatus)
                }}
                disabled={bulkActionInProgress}
                className="inline-flex items-center rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white transition-colors duration-150 disabled:opacity-50"
                style={{ background: 'var(--color-blue-deep, #7BA8C4)' }}
              >
                Proceed with bulk approval
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowImpactWarning(false)
                  setBulkApprovalImpact(null)
                }}
                className="inline-flex items-center rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors duration-150"
                style={{
                  background: 'var(--color-paper, #fff)',
                  borderColor: 'var(--color-rule, #E6E1D5)',
                  color: 'var(--color-navy, #212938)',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {drifts.length === 0 ? (
        <div className="px-7 py-16 text-center">
          <p
            className="text-base font-bold"
            style={{ color: 'var(--color-navy, #212938)' }}
          >
            Nothing in the queue.
          </p>
          <p
            className="mt-1 text-xs"
            style={{ color: 'var(--color-mute, #6B7280)' }}
          >
            Adjust your filters or run a new scan.
          </p>
        </div>
      ) : (
        <>
          {/* Group controls toolbar (small editorial summary, no chrome) */}
          {multiPropertyGroups.length > 0 && (
            <div
              className="flex items-center justify-between border-b px-7 py-2.5"
              style={{
                background: 'var(--color-paper, #fff)',
                borderColor: 'var(--color-rule-soft, #F0EBDD)',
              }}
            >
              <span
                className="text-[10px] font-bold uppercase tracking-[0.16em]"
                style={{ color: 'var(--color-mute, #6B7280)' }}
              >
                {groupedDrifts.length}{' '}
                {groupedDrifts.length === 1 ? 'resource' : 'resources'} ·{' '}
                <span className="font-mono normal-case tracking-normal">{drifts.length}</span>{' '}
                {drifts.length === 1 ? 'drift' : 'drifts'}
                {multiPropertyGroups.length > 0 && (
                  <span
                    className="ml-2"
                    style={{ color: 'var(--color-faint, #9CA3AF)' }}
                  >
                    ({multiPropertyGroups.length} grouped)
                  </span>
                )}
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setExpandedGroups(new Set(multiPropertyGroups.map((g) => g.groupKey)))
                  }
                  className="text-[10px] font-bold uppercase tracking-[0.14em] transition-colors duration-150"
                  style={{ color: 'var(--color-mute, #6B7280)' }}
                >
                  Expand all
                </button>
                <span style={{ color: 'var(--color-rule, #E6E1D5)' }}>·</span>
                <button
                  type="button"
                  onClick={() => setExpandedGroups(new Set())}
                  className="text-[10px] font-bold uppercase tracking-[0.14em] transition-colors duration-150"
                  style={{ color: 'var(--color-mute, #6B7280)' }}
                >
                  Collapse all
                </button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th
                    className="w-10 py-3.5 pl-7 pr-2 text-left text-[9px] font-bold uppercase tracking-[0.18em]"
                    style={{
                      background: 'var(--color-paper-warm, #FBF8F2)',
                      borderBottom: '1px solid var(--color-rule, #E6E1D5)',
                      color: 'var(--color-mute, #6B7280)',
                    }}
                  >
                    <DriftCheckbox
                      ariaLabel="Select all drifts"
                      checked={selectedDrifts.size === drifts.length && drifts.length > 0}
                      indeterminate={
                        selectedDrifts.size > 0 && selectedDrifts.size < drifts.length
                      }
                      onChange={(checked) => handleSelectAll(checked)}
                    />
                  </th>
                  <th
                    className="w-8 py-3.5 text-left text-[9px] font-bold uppercase tracking-[0.18em]"
                    style={{
                      background: 'var(--color-paper-warm, #FBF8F2)',
                      borderBottom: '1px solid var(--color-rule, #E6E1D5)',
                      color: 'var(--color-mute, #6B7280)',
                    }}
                  ></th>
                  <th
                    className="px-7 py-3.5 text-left text-[9px] font-bold uppercase tracking-[0.18em]"
                    style={{
                      background: 'var(--color-paper-warm, #FBF8F2)',
                      borderBottom: '1px solid var(--color-rule, #E6E1D5)',
                      color: 'var(--color-mute, #6B7280)',
                    }}
                  >
                    Setting
                  </th>
                  <th
                    className="px-3 py-3.5 text-left text-[9px] font-bold uppercase tracking-[0.18em]"
                    style={{
                      background: 'var(--color-paper-warm, #FBF8F2)',
                      borderBottom: '1px solid var(--color-rule, #E6E1D5)',
                      color: 'var(--color-mute, #6B7280)',
                    }}
                  >
                    Status
                  </th>
                  <th
                    className="px-3 py-3.5 text-left text-[9px] font-bold uppercase tracking-[0.18em]"
                    style={{
                      background: 'var(--color-paper-warm, #FBF8F2)',
                      borderBottom: '1px solid var(--color-rule, #E6E1D5)',
                      color: 'var(--color-mute, #6B7280)',
                    }}
                  >
                    Severity
                  </th>
                  <th
                    className="px-3 py-3.5 text-left text-[9px] font-bold uppercase tracking-[0.18em]"
                    style={{
                      background: 'var(--color-paper-warm, #FBF8F2)',
                      borderBottom: '1px solid var(--color-rule, #E6E1D5)',
                      color: 'var(--color-mute, #6B7280)',
                    }}
                  >
                    Detected
                  </th>
                  <th
                    className="px-7 py-3.5 text-left text-[9px] font-bold uppercase tracking-[0.18em]"
                    style={{
                      background: 'var(--color-paper-warm, #FBF8F2)',
                      borderBottom: '1px solid var(--color-rule, #E6E1D5)',
                      color: 'var(--color-mute, #6B7280)',
                    }}
                  >
                    Last action
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Multi-property resource groups */}
                {multiPropertyGroups.map((group) => {
                  const isExpanded = expandedGroups.has(group.groupKey)
                  const groupSelected = isGroupFullySelected(group)
                  const groupPartial = isGroupPartiallySelected(group)
                  const sevKey = SEV_KEY[group.highestSeverity]

                  return (
                    <Fragment key={group.groupKey}>
                      {/* Resource header row */}
                      <tr
                        className="cursor-pointer transition-colors duration-150"
                        style={{
                          background: 'var(--color-paper-warm, #FBF8F2)',
                          borderBottom: '1px solid var(--color-rule, #E6E1D5)',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background =
                            'var(--color-rule, #F5EFE3)'
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background =
                            'var(--color-paper-warm, #FBF8F2)'
                        }}
                        onClick={() => toggleGroup(group.groupKey)}
                      >
                        <td
                          className="py-3.5 pl-7 pr-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DriftCheckbox
                            ariaLabel={`Select all drifts in ${group.component}`}
                            checked={groupSelected}
                            indeterminate={groupPartial}
                            onChange={(checked) => handleSelectGroup(group, checked)}
                          />
                        </td>
                        <td className="py-3.5 pl-1 pr-1">
                          <ChevronDownIcon
                            className={clsx(
                              'h-4 w-4 transition-transform duration-150',
                              !isExpanded && '-rotate-90',
                            )}
                            style={{ color: 'var(--color-mute, #6B7280)' }}
                          />
                        </td>
                        <td className="px-7 py-3.5" colSpan={3}>
                          <div className="flex items-center gap-3.5">
                            <span
                              className="block h-7 w-1 flex-shrink-0 rounded-full"
                              style={{ background: SEV_RAIL_COLOR[sevKey] }}
                              aria-hidden
                            />
                            <div className="min-w-0">
                              <div
                                className="truncate text-sm font-bold"
                                style={{ color: 'var(--color-navy, #212938)' }}
                              >
                                {group.component}
                                <em
                                  className="ml-1 font-serif font-semibold"
                                  style={{
                                    fontStyle: 'italic',
                                    color: 'var(--color-green-deep, #4A9B6E)',
                                    fontFamily:
                                      'var(--font-serif, "Fraunces", "Inter", serif)',
                                  }}
                                >
                                  · {group.drifts.length}{' '}
                                  {group.drifts.length === 1 ? 'setting' : 'settings'} drifted
                                </em>
                              </div>
                              {group.componentName !== group.component && (
                                <div
                                  className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] truncate"
                                  style={{ color: 'var(--color-mute, #6B7280)' }}
                                >
                                  {group.componentName}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3.5" colSpan={2}>
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(group.statusSummary).map(([status, count]) => (
                              <GsPill
                                key={status}
                                label={formatStatusLabel(status)}
                                count={count}
                              />
                            ))}
                          </div>
                        </td>
                        <td className="px-7 py-3.5">
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em]"
                            style={{ color: 'var(--color-mute, #6B7280)' }}
                          >
                            {group.drifts.length} items
                            <span
                              className="transition-transform duration-150"
                              aria-hidden
                            >
                              →
                            </span>
                          </span>
                        </td>
                      </tr>

                      {/* Child rows (when expanded) */}
                      {isExpanded &&
                        group.drifts.map((drift) => {
                          const childSelected = selectedDrifts.has(drift.id)
                          const isHighlighted = selectedId === drift.id
                          return (
                            <tr
                              key={drift.id}
                              className="transition-colors duration-150"
                              style={{
                                background: childSelected
                                  ? 'rgba(118,214,157,0.06)'
                                  : isHighlighted
                                    ? 'rgba(118,214,157,0.04)'
                                    : 'var(--color-paper, #fff)',
                                borderBottom: '1px solid var(--color-rule-soft, #F0EBDD)',
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLTableRowElement).style.background =
                                  childSelected
                                    ? 'rgba(118,214,157,0.1)'
                                    : 'var(--color-paper-warm, #FBF8F2)'
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLTableRowElement).style.background =
                                  childSelected
                                    ? 'rgba(118,214,157,0.06)'
                                    : isHighlighted
                                      ? 'rgba(118,214,157,0.04)'
                                      : 'var(--color-paper, #fff)'
                              }}
                            >
                              <td
                                className="py-3 pl-14 pr-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <DriftCheckbox
                                  ariaLabel={`Select drift ${drift.setting}`}
                                  checked={childSelected}
                                  onChange={(checked) => handleSelectDrift(drift.id, checked)}
                                />
                              </td>
                              <td className="py-3"></td>
                              <td
                                className="relative cursor-pointer py-3 pl-14 pr-7"
                                onClick={() => onRowClick(drift.id)}
                              >
                                {/* dashed hairline rail under the group caret */}
                                <span
                                  aria-hidden
                                  className="pointer-events-none absolute bottom-0 top-0"
                                  style={{
                                    left: '44px',
                                    borderLeft: '1px dashed var(--color-rule, #E6E1D5)',
                                  }}
                                />
                                <div className="min-w-0">
                                  <div
                                    className="block truncate text-sm font-bold"
                                    style={{ color: 'var(--color-navy, #212938)' }}
                                    title={`${drift.component} · ${drift.setting}`}
                                  >
                                    {drift.component} · {drift.setting}
                                  </div>
                                </div>
                              </td>
                              <td
                                className="cursor-pointer px-3 py-3 whitespace-nowrap"
                                onClick={() => onRowClick(drift.id)}
                              >
                                <StatusPill status={drift.status} />
                              </td>
                              <td
                                className="cursor-pointer px-3 py-3 whitespace-nowrap"
                                onClick={() => onRowClick(drift.id)}
                              >
                                <SevPill severity={drift.severity} />
                              </td>
                              <td
                                className="cursor-pointer px-3 py-3 whitespace-nowrap font-mono text-[11px] font-semibold tabular-nums"
                                style={{ color: 'var(--color-faint, #9CA3AF)' }}
                                onClick={() => onRowClick(drift.id)}
                              >
                                {formatDistanceToNow(new Date(drift.detectedAt), {
                                  addSuffix: true,
                                })}
                              </td>
                              <td
                                className="cursor-pointer px-7 py-3 whitespace-nowrap text-xs"
                                onClick={() => onRowClick(drift.id)}
                                style={{ color: 'var(--color-mute, #6B7280)' }}
                              >
                                {drift.latestAuditEntry ? (
                                  <div>
                                    <div
                                      className="font-medium"
                                      style={{ color: 'var(--color-navy, #212938)' }}
                                    >
                                      {drift.latestAuditEntry.eventType}
                                    </div>
                                    <div className="text-[10px]">
                                      {drift.latestAuditEntry.triggeredBy}
                                    </div>
                                    <div
                                      className="font-mono text-[10px]"
                                      style={{ color: 'var(--color-faint, #9CA3AF)' }}
                                    >
                                      {formatDistanceToNow(
                                        new Date(drift.latestAuditEntry.eventTimestamp),
                                        { addSuffix: true },
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--color-faint, #9CA3AF)' }}>
                                    No actions
                                  </span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                    </Fragment>
                  )
                })}

                {/* Single-property drifts (flat rows — no group header) */}
                {singlePropertyDrifts.map((drift) => {
                  const childSelected = selectedDrifts.has(drift.id)
                  const isHighlighted = selectedId === drift.id
                  return (
                    <tr
                      key={drift.id}
                      className="transition-colors duration-150"
                      style={{
                        background: childSelected
                          ? 'rgba(118,214,157,0.06)'
                          : isHighlighted
                            ? 'rgba(118,214,157,0.04)'
                            : 'var(--color-paper, #fff)',
                        borderBottom: '1px solid var(--color-rule-soft, #F0EBDD)',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background =
                          childSelected
                            ? 'rgba(118,214,157,0.1)'
                            : 'var(--color-paper-warm, #FBF8F2)'
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background =
                          childSelected
                            ? 'rgba(118,214,157,0.06)'
                            : isHighlighted
                              ? 'rgba(118,214,157,0.04)'
                              : 'var(--color-paper, #fff)'
                      }}
                    >
                      <td
                        className="py-3.5 pl-7 pr-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DriftCheckbox
                          ariaLabel={`Select drift ${drift.setting}`}
                          checked={childSelected}
                          onChange={(checked) => handleSelectDrift(drift.id, checked)}
                        />
                      </td>
                      <td className="py-3.5"></td>
                      <td
                        className="cursor-pointer px-7 py-3.5"
                        onClick={() => onRowClick(drift.id)}
                      >
                        <div className="min-w-0">
                          <div
                            className="text-sm font-bold"
                            style={{ color: 'var(--color-navy, #212938)' }}
                          >
                            {drift.component} · {drift.setting}
                          </div>
                          {drift.componentName && drift.componentName !== drift.component && (
                            <div
                              className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]"
                              style={{ color: 'var(--color-mute, #6B7280)' }}
                            >
                              {drift.componentName}
                            </div>
                          )}
                        </div>
                      </td>
                      <td
                        className="cursor-pointer px-3 py-3.5 whitespace-nowrap"
                        onClick={() => onRowClick(drift.id)}
                      >
                        <StatusPill status={drift.status} />
                      </td>
                      <td
                        className="cursor-pointer px-3 py-3.5 whitespace-nowrap"
                        onClick={() => onRowClick(drift.id)}
                      >
                        <SevPill severity={drift.severity} />
                      </td>
                      <td
                        className="cursor-pointer px-3 py-3.5 whitespace-nowrap font-mono text-[11px] font-semibold tabular-nums"
                        style={{ color: 'var(--color-faint, #9CA3AF)' }}
                        onClick={() => onRowClick(drift.id)}
                      >
                        {formatDistanceToNow(new Date(drift.detectedAt), { addSuffix: true })}
                      </td>
                      <td
                        className="cursor-pointer px-7 py-3.5 whitespace-nowrap text-xs"
                        onClick={() => onRowClick(drift.id)}
                        style={{ color: 'var(--color-mute, #6B7280)' }}
                      >
                        {drift.latestAuditEntry ? (
                          <div>
                            <div
                              className="font-medium"
                              style={{ color: 'var(--color-navy, #212938)' }}
                            >
                              {drift.latestAuditEntry.eventType}
                            </div>
                            <div className="text-[10px]">
                              {drift.latestAuditEntry.triggeredBy}
                            </div>
                            <div
                              className="font-mono text-[10px]"
                              style={{ color: 'var(--color-faint, #9CA3AF)' }}
                            >
                              {formatDistanceToNow(new Date(drift.latestAuditEntry.eventTimestamp), {
                                addSuffix: true,
                              })}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--color-faint, #9CA3AF)' }}>
                            No actions yet
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination footer — paper-warm band, mono summary, circular pager */}
          {pagination.total > 0 && (
            <div
              className="flex flex-wrap items-center justify-between gap-3 border-t px-7 py-4"
              style={{
                background: 'var(--color-paper-warm, #FBF8F2)',
                borderColor: 'var(--color-rule, #E6E1D5)',
              }}
            >
              <div
                className="font-mono text-[11px] font-semibold tabular-nums"
                style={{ color: 'var(--color-mute, #6B7280)' }}
              >
                Showing{' '}
                <strong style={{ color: 'var(--color-navy, #212938)', fontWeight: 700 }}>
                  {showFromIndex}–{showToIndex}
                </strong>{' '}
                of{' '}
                <strong style={{ color: 'var(--color-navy, #212938)', fontWeight: 700 }}>
                  {pagination.total}
                </strong>{' '}
                {driftLabel}
              </div>

              <div className="inline-flex items-center gap-1.5">
                <PgArrow
                  direction="prev"
                  disabled={pagination.current === 1}
                  onClick={() => pagination.onPageChange(pagination.current - 1)}
                />
                {(() => {
                  const current = pagination.current
                  const pages: Array<number | 'ellipsis-start' | 'ellipsis-end'> = []
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) pages.push(i)
                  } else {
                    pages.push(1)
                    if (current > 3) pages.push('ellipsis-start')
                    for (
                      let i = Math.max(2, current - 1);
                      i <= Math.min(totalPages - 1, current + 1);
                      i++
                    ) {
                      pages.push(i)
                    }
                    if (current < totalPages - 2) pages.push('ellipsis-end')
                    pages.push(totalPages)
                  }
                  return pages.map((page) =>
                    typeof page === 'string' ? (
                      <span
                        key={page}
                        className="inline-flex h-[30px] w-[30px] items-center justify-center font-mono text-[11px]"
                        style={{ color: 'var(--color-faint, #9CA3AF)' }}
                      >
                        …
                      </span>
                    ) : (
                      <PgBtn
                        key={page}
                        page={page}
                        active={page === current}
                        onClick={() => pagination.onPageChange(page)}
                      />
                    ),
                  )
                })()}
                <PgArrow
                  direction="next"
                  disabled={pagination.current === totalPages}
                  onClick={() => pagination.onPageChange(pagination.current + 1)}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Bulk Action Comment Modal */}
      <BulkActionCommentModal
        isOpen={showCommentModal}
        onClose={() => {
          setShowCommentModal(false)
          setPendingBulkAction(null)
        }}
        onConfirm={executeBulkStatusChange}
        actionStatus={pendingBulkAction ?? ('Approved' as DriftStatus)}
        selectedCount={selectedDrifts.size}
        isProcessing={bulkActionInProgress}
      />

      {/* Rejection Notification Modal */}
      <RejectionNotificationModal
        isOpen={showRejectionNotification}
        onClose={() => setShowRejectionNotification(false)}
        rejectedCount={rejectedDriftCount}
        isBulkAction={rejectedDriftCount > 1}
      />
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Pagination atoms
   ──────────────────────────────────────────────────────────────────── */

function PgArrow({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next'
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'prev' ? 'Previous page' : 'Next page'}
      className={clsx(
        'inline-flex h-[30px] w-[30px] items-center justify-center rounded-full',
        'transition-colors duration-150',
        'disabled:cursor-not-allowed disabled:opacity-30',
      )}
      style={{ color: 'var(--color-mute, #6B7280)' }}
    >
      {direction === 'prev' ? (
        <ChevronLeftIcon className="h-4 w-4" />
      ) : (
        <ChevronRightIcon className="h-4 w-4" />
      )}
    </button>
  )
}

function PgBtn({
  page,
  active,
  onClick,
}: {
  page: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full border font-mono text-[11px] font-bold tabular-nums transition-colors duration-150"
      style={
        active
          ? {
              background: 'var(--color-navy, #212938)',
              borderColor: 'var(--color-navy, #212938)',
              color: 'var(--color-paper, #fff)',
            }
          : {
              background: 'var(--color-paper, #fff)',
              borderColor: 'var(--color-rule, #E6E1D5)',
              color: 'var(--color-mute, #6B7280)',
            }
      }
    >
      {page}
    </button>
  )
}
